import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuthStore } from '../../store/authStore'
import { Download } from 'lucide-react'
import { ConfirmModal, AlertModal } from '../../components/ConfirmModal'

export function ProfileTab() {
  const { user } = useAuthStore()
  const [profile, setProfile] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [confirmState, setConfirmState] = useState({
  open: false,
  title: '',
  message: '',
  action: () => {}
})

const [alertState, setAlertState] = useState({
  open: false,
  title: '',
  message: '',
  variant: 'info' as 'success' | 'danger' | 'info'
})

  useEffect(() => {
    loadProfile()
  }, [])

  async function loadProfile() {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user?.id)
      .single()

    setProfile(data)
    setLoading(false)
  }

  function isFormValid() {
    return (
      profile?.full_name &&
      profile?.phone &&
      profile?.house_number &&
      profile?.street &&
      profile?.city &&
      profile?.postcode
    )
  }

  async function saveProfile() {
    if (!isFormValid()) {
      setAlertState({
  open: true,
  title: 'Incomplete Form',
  message: 'Please complete all required fields.',
  variant: 'danger'
})
      return
    }

    setSaving(true)

    const { error } = await supabase
      .from('profiles')
      .update({
        full_name: profile.full_name,
        phone: profile.phone,
        house_number: profile.house_number,
        street: profile.street,
        city: profile.city,
        postcode: profile.postcode
      })
      .eq('id', user?.id)

    setSaving(false)

    if (!error) {
      setAlertState({
  open: true,
  title: 'Success',
  message: 'Profile updated successfully.',
  variant: 'success'
})
      loadProfile()
    } else {
      setAlertState({
  open: true,
  title: 'Error',
  message: 'Please update failed.',
  variant: 'danger'
})
      console.error(error)
    }
  }

  async function uploadFile(file: File, type: 'cv' | 'google') {
    const filePath = `${user?.id}/${type}-${Date.now()}`

    const { error } = await supabase.storage
      .from('documents')
      .upload(filePath, file)

    if (error) {
      setAlertState({
  open: true,
  title: 'Upload Failed',
  message: 'Please complete all required fields.',
  variant: 'danger'
})
      return
    }

    const column = type === 'cv' ? 'cv_file' : 'google_form_file'

    await supabase
      .from('profiles')
      .update({ [column]: filePath })
      .eq('id', user?.id)

    loadProfile()
    setAlertState({
  open: true,
  title: 'Success',
  message: 'File uploaded successfully.',
  variant: 'success'
})
  }

  async function downloadFile(path: string) {
    const { data } = await supabase.storage
      .from('documents')
      .createSignedUrl(path, 60)

    if (data?.signedUrl) {
      window.open(data.signedUrl, '_blank')
    }
  }

  if (loading) return <div className="text-center py-10">Loading...</div>

  return (
    <div className="max-w-2xl space-y-8">
      <h1 className="text-2xl font-bold">My Profile</h1>

      <div className="bg-white shadow rounded-xl p-6 space-y-4">

        {/* Email (read-only) */}
        <input
          value={profile?.email || ''}
          disabled
          className="w-full border p-2 rounded bg-gray-100 text-gray-500"
        />

        <input
          placeholder="Enter Your Full Name *"
          value={profile?.full_name || ''}
          onChange={(e) =>
            setProfile({ ...profile, full_name: e.target.value })
          }
          className="w-full border p-2 rounded"
        />

        

        <input
          placeholder="Phone Number *"
          value={profile?.phone || ''}
          onChange={(e) =>
            setProfile({ ...profile, phone: e.target.value })
          }
          className="w-full border p-2 rounded"
        />

        <input
          placeholder="House Number *"
          value={profile?.house_number || ''}
          onChange={(e) =>
            setProfile({ ...profile, house_number: e.target.value })
          }
          className="w-full border p-2 rounded"
        />

        <input
          placeholder="Street *"
          value={profile?.street || ''}
          onChange={(e) =>
            setProfile({ ...profile, street: e.target.value })
          }
          className="w-full border p-2 rounded"
        />

        <input
          placeholder="City *"
          value={profile?.city || ''}
          onChange={(e) =>
            setProfile({ ...profile, city: e.target.value })
          }
          className="w-full border p-2 rounded"
        />

        <input
          placeholder="Postcode *"
          value={profile?.postcode || ''}
          onChange={(e) =>
            setProfile({ ...profile, postcode: e.target.value })
          }
          className="w-full border p-2 rounded"
        />

        <button
  onClick={() =>
    setConfirmState({
      open: true,
      title: 'Update Profile',
      message: 'Are you sure you want to update your profile details?',
      action: saveProfile
    })
  }
  disabled={saving}
  className="bg-nhs-blue text-white px-4 py-2 rounded"
>
          {saving ? 'Saving...' : 'Save Profile'}
        </button>
      </div>

      <div className="bg-white shadow rounded-xl p-6 space-y-4">
        <h2 className="font-semibold">Upload Your CV</h2>
        <h2 className="font-semibold">Upload Downloaded Google Form</h2>

        <div>
          <input
            type="file"
            onChange={(e) =>
              e.target.files && uploadFile(e.target.files[0], 'cv')
            }
          />

          {profile?.cv_file && (
            <button
              onClick={() => downloadFile(profile.cv_file)}
              className="text-blue-600 flex items-center gap-1 mt-2"
            >
              <Download className="h-4 w-4" /> Download CV
            </button>
          )}
        </div>

        <div>
          <input
            type="file"
            onChange={(e) =>
              e.target.files && uploadFile(e.target.files[0], 'google')
            }
          />

          {profile?.google_form_file && (
            <button
              onClick={() => downloadFile(profile.google_form_file)}
              className="text-blue-600 flex items-center gap-1 mt-2"
            >
              <Download className="h-4 w-4" /> Download Google Form
            </button>
          )}
        </div>
      </div>
      <ConfirmModal
  open={confirmState.open}
  title={confirmState.title}
  message={confirmState.message}
  onConfirm={() => {
    confirmState.action()
    setConfirmState(prev => ({ ...prev, open: false }))
  }}
  onCancel={() =>
    setConfirmState(prev => ({ ...prev, open: false }))
  }
/>

<AlertModal
  open={alertState.open}
  title={alertState.title}
  message={alertState.message}
  variant={alertState.variant}
  onClose={() =>
    setAlertState(prev => ({ ...prev, open: false }))
  }
/>
    </div>
  )
}